import React, { useState, useEffect } from 'react';
import { useContent } from '../contexts/ContentContext';
import {
  X, Clock, Video,
  Globe, ShieldCheck, Loader2
} from 'lucide-react';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType: string;
}

export const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, defaultType }) => {
  const { lang, consent, updateConsent } = useContent();
  const [iframeLoading, setIframeLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(consent.marketing);

  // The link provided by the user
  const calendarLink = "https://calendar.google.com/appointments/schedules/AcZssZ2n9flby8dzRMuH6JssukDN8gLkCDIiKl2jxEPysgUPWsqQKSss9WzGRb00K5k7lBRNk69qjLfU";

  useEffect(() => {
    if (consent.marketing) {
      setShowCalendar(true);
    }
  }, [consent.marketing]);

  const handleEnableCalendar = () => {
    updateConsent({ marketing: true });
    setShowCalendar(true);
  };

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIframeLoading(true);
    } else {
      document.body.style.overflow = '';
      // Reset state if needed, or keep it persisted if consent was given
      if (!consent.marketing) setShowCalendar(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, consent.marketing]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-2 sm:p-6 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0d0a0f]/95 backdrop-blur-xl" onClick={onClose}></div>

      {/* Modal Container */}
      <div className="relative bg-[#1a141f] border border-white/10 rounded-[32px] md:rounded-[48px] w-full max-w-6xl h-[95vh] sm:h-[90vh] shadow-[0_0_100px_rgba(0,0,0,1)] overflow-hidden animate-in fade-in zoom-in duration-500 bg-grid flex flex-col md:flex-row">

        {/* Improved Close button with higher contrast for white backgrounds */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-900 hover:text-accent transition-all z-[90] bg-white/90 p-3 rounded-full backdrop-blur-md shadow-2xl border border-gray-200 hover:border-accent hover:scale-110 active:scale-95 group"
          aria-label="Close"
        >
          <X size={24} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
        </button>

        {/* Left Side: Call Info (Context Panel) */}
        <div className="hidden md:flex w-full md:w-[350px] bg-[#120e16] p-10 border-r border-white/5 flex-col justify-between relative shrink-0">
          <div className="absolute top-0 left-0 w-full h-full bg-grid opacity-10 pointer-events-none"></div>

          <div className="relative z-10">
            <div className="w-14 h-14 bg-accent/10 border border-accent/20 rounded-2xl flex items-center justify-center text-accent mb-10 shadow-[0_0_20px_rgba(249,115,22,0.2)]">
              <Video size={24} />
            </div>
            <h2 className="text-3xl font-gaming font-bold text-white uppercase tracking-tighter leading-none mb-6">
              Technical <br /><span className="text-accent">Discovery Call</span>
            </h2>
            <p className="text-gray-400 text-sm font-light leading-relaxed mb-10 italic">
              {lang === 'en'
                ? "Schedule a strategic deep-dive with our engineering team to analyze your project scope and objectives."
                : "Rezervišite strateški termin sa našim inženjerskim timom za analizu opsega i ciljeva vašeg projekta."}
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4 text-gray-400 group">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center text-accent group-hover:border-accent/30 transition-colors">
                  <Clock size={14} />
                </div>
                <span className="font-bold uppercase tracking-[0.2em] text-[10px]">30 Minutes Session</span>
              </div>
              <div className="flex items-center gap-4 text-gray-400 group">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center text-accent group-hover:border-accent/30 transition-colors">
                  <Globe size={14} />
                </div>
                <span className="font-bold uppercase tracking-[0.2em] text-[10px]">Google Meet Conference</span>
              </div>
              <div className="flex items-center gap-4 text-gray-400 group">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center text-accent group-hover:border-accent/30 transition-colors">
                  <ShieldCheck size={14} />
                </div>
                <span className="font-bold uppercase tracking-[0.2em] text-[10px]">Confidential - NDA Secure</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Google Calendar Embed */}
        <div
          className="flex-grow relative bg-white"
          style={{ colorScheme: 'light' }}
        >
          {!showCalendar ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-white p-6 text-center">
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6">
                <ShieldCheck size={40} className="text-blue-600" />
              </div>
              <h3 className="text-gray-900 font-bold text-xl mb-2 uppercase tracking-tight">External Content</h3>
              <p className="text-gray-500 max-w-md mb-8">
                {lang === 'en'
                  ? "This content is hosted by Google Calendar. To view it, you must accept marketing cookies."
                  : "Ovaj sadržaj hostuje Google Kalendar. Da biste ga vidjeli, morate prihvatiti marketinške kolačiće."}
              </p>
              <button
                id="cta-booking-accept-cookies"
                onClick={handleEnableCalendar}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-500/30 text-xs"
              >
                {lang === 'en' ? "Accept & Load Calendar" : "Prihvati i Učitaj"}
              </button>
            </div>
          ) : (
            <>
              {iframeLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-white">
                  <div className="relative">
                    <Loader2 size={48} className="text-accent animate-spin mb-4" />
                    <div className="absolute inset-0 blur-xl bg-accent/20 animate-pulse"></div>
                  </div>
                  <p className="text-gray-400 font-gaming font-bold uppercase tracking-[0.3em] text-[10px] animate-pulse">
                    Initializing Global Calendar...
                  </p>
                  {/* Fallback link if loading takes too long */}
                  <a
                    href={calendarLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 text-xs text-blue-500 underline opacity-0 animate-in fade-in delay-1000 duration-1000"
                  >
                    If not loading, click here to open in new tab
                  </a>
                </div>
              )}

              <div className="w-full h-full overflow-hidden shadow-[inset_0_0_40px_rgba(0,0,0,0.05)]">
                <iframe
                  src={calendarLink}
                  className={`w-full h-full border-none transition-opacity duration-1000 ${iframeLoading ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={() => setIframeLoading(false)}
                  title="Book a Meeting with Saltium Games"
                  allow="camera; microphone; fullscreen; payment; geolocation"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
                />
              </div>
            </>
          )}

          {/* Fallback for mobile if iframe fails or feels too tight */}
          <div className="md:hidden absolute bottom-4 left-4 right-4 z-[80]">
            <a
              id="cta-booking-open-external"
              href={calendarLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full py-4 text-[10px] shadow-2xl"
            >
              Open in New Window
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};